/**
 * Кто говорит (07-оболочка-тз, «Кто говорит», «Метка говорящего и отдельная
 * колонка»).
 *
 *   `> марго — Нет.`        Марго
 *   `> тоби — Маннитол.`    названный собеседник
 *   `> «Единственный...».`  цитата документа или книги, а не речь
 *   обычный абзац           ремарка, описание, действие
 *
 * **Метка обязательна у каждой устной реплики, включая Марго.** Разговор один
 * на один исключением не является: прокрутка назад, ремарки между строками
 * и появление второго собеседника не должны менять грамматику текста задним
 * числом. Отсюда же цитата: `>` без метки говорит не человек — это бумага,
 * надпись, строка из учебника.
 *
 * Определение одно на всю игру: этим же признаком поток красится на экране и по
 * нему же предпросмотр ищет реплику Марго. Разъедься эти два места — и в области
 * деталей однажды окажется ремарка, поданная как то, что Марго скажет.
 */

/** Имена совпадают с классами палитры: цвет роли задан в game.yaml. */
export type Voice = 'speech' | 'margo' | 'quote' | 'remark';

/** Устная реплика и цитата пишутся цитатой Markdown — в Obsidian так и надо. */
const QUOTE = /^\s*>\s?/;

/** Метка говорящего: всё, что стоит между `> ` и первым ` — `. */
const NAMED = /^([^—]{1,40}?)\s+—\s/;

/** Имя Марго в метке: её реплика остаётся своим цветом, остальные — общим. */
export const MARGO = 'марго';

export interface Said {
  voice: Voice;
  /** Кто говорит. `null` — цитата или ремарка: человека за строкой нет. */
  name: string | null;
  /** Сама строка без служебных знаков и без метки. */
  text: string;
}

/**
 * Разбор строки потока. Метка — служебное имя, а не реплика: пишется строчными,
 * как команда, и капитализацию решает оболочка.
 */
export function speakerOf(line: string): Said {
  if (!QUOTE.test(line)) return { voice: 'remark', name: null, text: line };

  const body = line.replace(QUOTE, '');
  const named = NAMED.exec(body);
  // Без метки — цитата: бумага, надпись, строка из учебника. Безымянных
  // устных реплик в игре нет, и валидатор за этим следит.
  if (!named) return { voice: 'quote', name: null, text: body };

  const name = named[1]!.trim();
  return {
    voice: name.toLowerCase() === MARGO ? 'margo' : 'speech',
    name,
    // Тире — граница метки, а не знак речи: оболочка его не печатает, эту
    // работу делает колонка (07-оболочка-тз, «Метка говорящего»).
    text: body.slice(named[0].length).trimStart(),
  };
}

export function voiceOf(line: string): Voice {
  return speakerOf(line).voice;
}

/**
 * Строка без служебных знаков. Знак цитаты снимается целиком: `>` на экране
 * занят приглашением ввода, а кто говорит, видно по колонке с именем.
 */
export function said(line: string): string {
  return speakerOf(line).text;
}

/**
 * Как метка выглядит на экране: имя пишут строчными, показывают прописными.
 *
 * `speakers` — объявленные эпизодом сокращения (`полицейский → полиц.`):
 * колонка говорящего узкая, а метка в тексте остаётся человеческой. Сокращение
 * задаёт автор, оболочка его не выдумывает.
 */
export function speakerLabel(name: string, speakers: Record<string, string> = {}): string {
  return (speakers[name.toLowerCase()] ?? name).toUpperCase();
}
